import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { fetchChatMedia } from "../../../actions/chatActions";
import SiteMediaViewer from "../../../components/media/SiteMediaViewer";
import Attachment from "../Attachment";
import {
    downloadBlob,
    fetchChatAttachment,
    getChatAttachmentName,
    getChatAttachmentMimeType,
    getChatAttachmentType,
    hasSecureChatAttachment,
    shareBlobFile,
} from "../../../utils/chatAttachmentStore";

const labels = {
    image: "Фотографии",
    video: "Видео",
    doc: "Документы",
    voice: "Голосовые",
    link: "Ссылки",
};

const getSenderName = (item) => {
    const sender = item?.sender;

    if (!sender) return item?.guestSender?.name || item?.senderName || "Неизвестно";

    const name = [sender.firstName, sender.lastName].filter(Boolean).join(" ").trim();

    return name || sender.email || sender.username || item?.senderName || "Неизвестно";
};

const formatDate = (value) => {
    if (!value) return "";

    return new Date(value).toLocaleDateString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
    });
};

export default function MediaModal({ chatId, type = "image", onClose }) {
    const dispatch = useDispatch();
    const scrollRef = useRef(null);
    const loadingRef = useRef(false);

    const [items, setItems] = useState([]);
    const [cursor, setCursor] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [previewId, setPreviewId] = useState("");

    const load = useCallback(async (before = null) => {
        if (!chatId || loadingRef.current) return;

        loadingRef.current = true;
        setLoading(true);
        setError("");

        try {
            const data = await dispatch(fetchChatMedia(chatId, { type, before, limit: 60 }));
            const nextItems = Array.isArray(data?.items) ? data.items : [];

            setItems((prev) => before ? [...prev, ...nextItems] : nextItems);
            setCursor(data?.nextCursor || null);
        } catch (requestError) {
            setError(requestError?.response?.data?.error || requestError?.message || "Не удалось загрузить медиа");
            if (!before) setItems([]);
            setCursor(null);
        } finally {
            loadingRef.current = false;
            setLoading(false);
        }
    }, [dispatch, chatId, type]);

    useEffect(() => {
        setItems([]);
        setCursor(null);
        setPreviewId("");
        setError("");
        load(null);
    }, [chatId, type, load]);

    useEffect(() => {
        const el = scrollRef.current;

        if (!el) return undefined;

        const handler = () => {
            if (loading || !cursor) return;
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 160) load(cursor);
        };

        el.addEventListener("scroll", handler, { passive: true });

        return () => el.removeEventListener("scroll", handler);
    }, [cursor, loading, load]);

    useEffect(() => {
        const handler = (event) => {
            if (event.key === "Escape" && !previewId) onClose?.();
        };

        window.addEventListener("keydown", handler);

        return () => window.removeEventListener("keydown", handler);
    }, [previewId, onClose]);

    const attachmentItems = useMemo(
        () => items.filter(hasSecureChatAttachment),
        [items]
    );

    const previewItems = useMemo(
        () => attachmentItems.filter((item) => {
            const attachmentType = getChatAttachmentType(item);
            if (type === "image") return attachmentType === "image";
            if (type === "video") return attachmentType === "video";
            if (type === "voice") return attachmentType === "voice";
            if (type === "doc") return attachmentType === "doc";
            return false;
        }),
        [attachmentItems, type]
    );

    const previewIndex = Math.max(
        0,
        previewItems.findIndex((item) => String(item?._id || "") === String(previewId))
    );

    const handleDownload = useCallback(async (message) => {
        const asset = await fetchChatAttachment({ message, download: true });
        downloadBlob({
            blob: asset.blob,
            filename: asset.filename || getChatAttachmentName(message),
        });
    }, []);

    const handleShare = useCallback(async (message) => {
        const asset = await fetchChatAttachment({ message });
        await shareBlobFile({
            blob: asset.blob,
            filename: asset.filename || getChatAttachmentName(message),
            mimeType: asset.mimeType || getChatAttachmentMimeType(message),
            title: labels[type] || "Медиа чата",
        });
    }, [type]);

    return (
        <div className="chat-modal-AO" onMouseDown={onClose}>
            <div className="chat-modal-AO__body" onMouseDown={(event) => event.stopPropagation()}>
                <div className="chat-modal-AO__head">
                    <div className="chat-modal-AO__title">{labels[type] || "Медиа"}</div>
                    <button type="button" onClick={onClose}>✕</button>
                </div>

                <div ref={scrollRef} className="chat-modal-AO__content">
                    {type === "image" && (
                        <div className="media-grid-AO media-grid-AO--image">
                            {attachmentItems.map((item) => (
                                <div key={item._id} className="media-card-AO media-card-AO--image">
                                    <Attachment
                                        message={item}
                                        onOpen={() => setPreviewId(String(item._id))}
                                    />
                                    <span>{getSenderName(item)}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {type === "video" && (
                        <div className="media-grid-AO media-grid-AO--video">
                            {attachmentItems.map((item) => (
                                <div key={item._id} className="media-card-AO media-card-AO--video">
                                    <Attachment message={item} onOpen={() => setPreviewId(String(item._id))} />
                                    <div>{getSenderName(item)}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    {type === "voice" && (
                        <div className="media-list-AO">
                            {attachmentItems.map((item) => (
                                <div key={item._id} className="media-row-AO">
                                    <div>
                                        <b>{getSenderName(item)}</b>
                                        <span>{formatDate(item.createdAt)}</span>
                                    </div>
                                    <Attachment message={item} onOpen={() => setPreviewId(String(item._id))} />
                                </div>
                            ))}
                        </div>
                    )}

                    {type === "doc" && (
                        <div className="media-list-AO">
                            {attachmentItems.map((item) => (
                                <div key={item._id} className="media-row-AO media-row-AO--doc">
                                    <Attachment message={item} onOpen={() => setPreviewId(String(item._id))} />
                                    <small>{getSenderName(item)} · {formatDate(item.createdAt)}</small>
                                </div>
                            ))}
                        </div>
                    )}

                    {type === "link" && (
                        <div className="media-list-AO">
                            {items.map((item) => {
                                const links = (item.text || "").match(/https?:\/\/\S+|www\.\S+/gi) || [];

                                if (!links.length) return null;

                                return (
                                    <div key={item._id} className="media-row-AO media-row-AO--link">
                                        <b>{getSenderName(item)}</b>
                                        {links.map((link, index) => {
                                            const href = link.startsWith("http") ? link : `https://${link}`;

                                            return <a key={`${item._id}-${index}`} href={href} target="_blank" rel="noreferrer">{link}</a>;
                                        })}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {error && <div className="chat-search-AO__error" role="alert">{error}</div>}
                    {loading && <div className="chat-modal-AO__hint">Загрузка…</div>}
                    {!loading && !items.length && !error && <div className="chat-modal-AO__hint">Ничего нет</div>}
                </div>
            </div>

            <SiteMediaViewer
                open={type !== "link" && !!previewId && previewItems.length > 0}
                items={previewItems}
                activeIndex={previewIndex}
                onActiveIndexChange={(nextIndex, nextItem) => setPreviewId(String(nextItem?._id || ""))}
                onClose={() => setPreviewId("")}
                resolveItem={(item) => fetchChatAttachment({ message: item })}
                getItemMimeType={(item) => getChatAttachmentMimeType(item) || "application/octet-stream"}
                getTitle={(item) => getChatAttachmentName(item, "Файл")}
                getSubtitle={(item) => `${getSenderName(item)} · ${formatDate(item?.createdAt)}`}
                getAlt={(item) => getChatAttachmentName(item, "Файл")}
                onDownload={handleDownload}
                onShare={handleShare}
                shareLabel="Поделиться"
                loadingText="Загружаем защищённый файл…"
                emptyText="Файл недоступен"
            />
        </div>
    );
}
